/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  bulkUploadEntities,
  bulkUploadEntitiesNoCheck,
  checkEntityDuplicate,
  createBulkEntities,
  createBulkEntitiesWithoutDuplicateCheck,
  createEntity,
  getEntitiesByDatabaseId,
  getEntitiesByGroupId,
  getEntityById,
  getEntityChildren,
  getEntityHierarchy,
  getProductsBySupplierId,
  getSuppliersByProductId,
  updateEntitiesStatusFromTo,
  previewEntitiesStatusFromTo,
  updateEntity,
  updateEntityStatus,
  restorePreviousStatus,
  declinePreviousStatus,
  restorePreviousStatusByFlag,
  removeDatabaseEntities,
  updateDatabaseEntitiesStatus,
} from './entities.service';
import { hasKeyPrefix, Time } from '../shared';
import EntityServiceAPI from '../../EntityServiceAPI';
import { IEntity, PaginationParams } from './entities';
import { databaseKeys, useInvalidateAllDatabaseKeys } from '../database/database.hooks';
import { STATS_QUERY_KEYS } from '../stats/stats.hooks';
import { auditKeys } from '../audit';
import { entityKeys, invalidateEntityLists } from './entities.keys';
import { StatusFlag } from './statusFlags';

export { entityKeys, invalidateEntityLists };

/**
 * Shared options for the heavy entity list queries. The list is refetched by
 * invalidation (mutations, screening phase changes), not by focus or by a
 * zero stale time, and the previous page stays on screen while the next one
 * loads -- but only within the same list scope (the same database or team),
 * so another database's rows never flash in.
 */
export const ENTITY_LIST_STALE_TIME = 30 * Time.SECOND;

const keepPreviousWithinScope =
  (scopePrefix: readonly unknown[]) =>
  (previousData: any, previousQuery: any) => {
    const previousKey: readonly unknown[] = previousQuery?.queryKey || [];
    return hasKeyPrefix(previousKey, scopePrefix) ? previousData : undefined;
  };

export interface EntityListQueryOptions {
  /** False keeps the query idle (no request), e.g. while its tab is hidden. */
  enabled?: boolean;
}

// Hook to get entities by database ID with optional search filtering
export const useGetEntitiesByDatabaseId = (
  databaseId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
  options: EntityListQueryOptions = {},
) => {
  const search = searchTerm.trim();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: entityKeys.entitiesByDatabase(databaseId, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: search,
      tab: paginationParams.tab,
      statusFilters: paginationParams.statusFilters,
      riskFilters: paginationParams.riskFilters,
      hasNewResults: paginationParams.hasNewResults,
      impactedByError: paginationParams.impactedByError,
      statusReset: paginationParams.statusReset,
      previousStatus: paginationParams.previousStatus,
      lensFilters: paginationParams.lensFilters,
      hitCountRanges: paginationParams.hitCountRanges,
      nameStartsWith: paginationParams.nameStartsWith,
    }),
    queryFn: () =>
      getEntitiesByDatabaseId(databaseId, {
        ...paginationParams,
        search: search,
      }),
    staleTime: ENTITY_LIST_STALE_TIME,
    gcTime: 5 * Time.MINUTE,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousWithinScope(
      entityKeys.entitiesByDatabasePrefix(databaseId),
    ),
    enabled: Boolean(databaseId) && (options.enabled ?? true),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get entities by group ID with optional search filtering
export const useGetEntitiesByGroupId = (
  groupId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const search = searchTerm.trim();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: entityKeys.entitiesByGroup(groupId, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: search,
      databaseName: paginationParams.databaseName,
      exlusions: paginationParams.exclusions,
      statusFilters: paginationParams.statusFilters,
      riskFilters: paginationParams.riskFilters,
      hasNewResults: paginationParams.hasNewResults,
      statusReset: paginationParams.statusReset,
      previousStatus: paginationParams.previousStatus,
      lensFilters: paginationParams.lensFilters,
      hitCountRanges: paginationParams.hitCountRanges,
      nameStartsWith: paginationParams.nameStartsWith,
      entitySearchTerms: paginationParams.entitySearchTerms,
      createdSince: paginationParams.createdSince,
    }),
    queryFn: () =>
      getEntitiesByGroupId(groupId, {
        ...paginationParams,
        search: search,
      }),
    staleTime: ENTITY_LIST_STALE_TIME,
    gcTime: 5 * Time.MINUTE,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousWithinScope(
      entityKeys.entitiesByGroupPrefix(groupId),
    ),
    enabled: Boolean(groupId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get entity by ID
export const useGetEntityById = (
  id?: string,
  shouldRun?: boolean,
  databaseId?: string,
  selectedTeamId?: string,
): UseQueryResult<IEntity | null> => {
  return useQuery({
    queryKey: entityKeys.entityById(id || '', databaseId, selectedTeamId),
    queryFn: () => getEntityById(id, databaseId, selectedTeamId),
    staleTime: 20 * Time.MINUTE,
    enabled: Boolean(id) && (shouldRun ?? true),
  });
};

export const useGetEntityOwnershipMatches = (
  id?: string | number,
  page = 0,
  size = 100,
) => {
  const entityId = String(id || '');
  const ownershipQueryKey = entityKeys.ownershipMatches(entityId);

  return useQuery({
    queryKey: entityKeys.ownershipMatchesPage(entityId, page, size),
    queryFn: () =>
      EntityServiceAPI.getEntityOwnershipMatches(entityId, page, size),
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 30_000,
    placeholderData: (previousData: any, previousQuery: any) => {
      const previousKey = previousQuery?.queryKey || [];
      const isSameEntity = ownershipQueryKey.every(
        (keyPart, index) => previousKey[index] === keyPart,
      );

      return isSameEntity ? previousData : undefined;
    },
  });
};

/**
 * User-triggered AI evaluation of the ownership/PEP rows already saved for a
 * DatabaseEntity. On success entity queries are invalidated so the persisted
 * `ownership_info` re-loads.
 */
export const useEvaluateOwnership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      databaseId: string;
      databaseEntityId: string;
      force?: boolean;
    }) => EntityServiceAPI.evaluateOwnership(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: entityKeys.entity() });
      queryClient.invalidateQueries({
        queryKey: entityKeys.ownershipMatches(variables.databaseEntityId),
      });
    },
  });
};

export const useGetEntityHierarchy = (
  id?: string,
  options?: {
    direction?: 'above' | 'below' | 'both';
    maxDepth?: number;
  },
): UseQueryResult<{ above?: IEntity[]; below?: IEntity[] }> => {
  const direction = options?.direction ?? 'both';
  const maxDepth = options?.maxDepth ?? 20;

  return useQuery({
    queryKey: entityKeys.entityHierarchy(id || '', direction, maxDepth),
    queryFn: () =>
      getEntityHierarchy(id, { direction: direction, maxDepth: maxDepth }),
    staleTime: 5 * Time.MINUTE,
    enabled: Boolean(id),
  });
};

export const useGetEntityChildren = (
  entityId: string | undefined,
  databaseId: string | undefined,
  selectedTeamId: string | undefined,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: entityKeys.entityChildren(entityId, databaseId, selectedTeamId),
    queryFn: () => getEntityChildren(entityId!, databaseId!, selectedTeamId!),
    enabled: Boolean(entityId && databaseId && selectedTeamId && enabled),
    staleTime: 60_000,
  });
};

// Hook to get suppliers by product ID
export const useGetSuppliersByProductId = (productId?: string) => {
  return useQuery({
    queryKey: entityKeys.suppliersByProduct(productId || ''),
    queryFn: () => getSuppliersByProductId(productId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(productId),
  });
};

// Hook to get products by supplier ID
export const useGetProductsBySupplierId = (supplierId?: string) => {
  return useQuery({
    queryKey: entityKeys.productsBySupplier(supplierId || ''),
    queryFn: () => getProductsBySupplierId(supplierId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(supplierId),
  });
};

// Mutation hook to update entity
export const useUpdateEntity = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      entityData,
    }: {
      id: string;
      entityData: Partial<any>;
    }) => updateEntity(id, entityData),
    onSuccess: (data, variables) => {
      if (databaseId) {
        invalidateEntityLists(queryClient, databaseId);
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: entityKeys.all() });
      }

      // Invalidate the specific entity
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityById(variables.id),
      });

      // Invalidate children cache so expanded child rows reflect updates
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityChildrenAll(),
        exact: false,
      });

      // Invalidate entity hierarchy so the relationship graph reflects name/detail changes
      queryClient.invalidateQueries({
        queryKey: [...entityKeys.entity(), 'hierarchy', variables.id],
        exact: false,
      });

      // Invalidate entity relationships so related-entity names update in the graph
      queryClient.invalidateQueries({
        queryKey: ['entity-relationship'],
        exact: false,
      });

      // Invalidate audit logs for this entity
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByEntity(variables.id),
        exact: false,
      });

      // Also invalidate database-level audit logs if we have databaseId
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(databaseId),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to update entity status
export const useUpdateEntityStatus = (
  databaseId?: string,
  selectedTeamId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      statusData,
    }: {
      id: string;
      statusData: { status_flag: string };
    }) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return updateEntityStatus(id, statusData, selectedTeamId);
    },
    onSuccess: (data, variables) => {
      if (databaseId) {
        invalidateEntityLists(queryClient, databaseId);
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: entityKeys.all() });
      }

      // Invalidate children cache so expanded child rows reflect the new status
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityChildrenAll(),
        exact: false,
      });

      // Invalidate the specific entity by ID
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityById(variables.id),
      });

      // Invalidate audit logs for this entity
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByEntity(variables.id),
        exact: false,
      });

      // Also invalidate database-level audit logs if we have databaseId
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(databaseId),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to create a single entity
export const useCheckEntityDuplicate = () => {
  return useMutation({
    mutationFn: ({
      payload,
      teamId,
    }: {
      payload: {
        name: string;
        country_of_origin?: string;
        database_id: string;
      };
      teamId?: string;
    }) => checkEntityDuplicate(payload, teamId),
  });
};

export const useCreateEntity = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      entityData,
      teamId,
    }: {
      entityData: any;
      teamId?: string;
    }) => createEntity(entityData, teamId),
    onSuccess: (data, variables) => {
      // Use comprehensive invalidation for all database-related keys
      invalidateAllDatabaseKeys(variables.teamId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate audit logs for the new entity
      // Note: createEntity returns boolean, so we can't get the entity ID from response
      // The audit logs will be invalidated by database-level invalidation below

      // Invalidate database-level audit logs if we have database_id
      if (variables.entityData?.database_id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(
            variables.entityData.database_id,
          ),
          exact: false,
        });
      }
    },
  });
};

// Mutation hook to bulk upload entities
export const useBulkUploadEntities = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      databaseId,
      file,
      createdBy,
      teamId,
    }: {
      databaseId: string;
      file: File;
      createdBy?: string;
      teamId?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('database_id', databaseId);
      if (createdBy) {
        formData.append('created_by', createdBy);
      }
      return bulkUploadEntities(formData, teamId);
    },
    onSuccess: (_, variables) => {
      invalidateAllDatabaseKeys(variables.teamId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate database-level audit logs for bulk upload
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.databaseId),
        exact: false,
      });

      // Invalidate all entity audit logs since we don't know specific IDs for bulk upload
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to bulk upload entities
export const useBulkUploadEntitiesNoCheck = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      databaseId,
      file,
      createdBy,
      teamId,
    }: {
      databaseId: string;
      file: File;
      createdBy?: string;
      teamId?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('database_id', databaseId);
      if (createdBy) {
        formData.append('created_by', createdBy);
      }
      return bulkUploadEntitiesNoCheck(formData, teamId);
    },
    onSuccess: (_, variables) => {
      invalidateAllDatabaseKeys(variables.teamId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate database-level audit logs for bulk upload
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.databaseId),
        exact: false,
      });

      // Invalidate all entity audit logs since we don't know specific IDs for bulk upload
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to create bulk entities without duplicate check
export const useCreateBulkEntities = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      entities,
      databaseId,
      createdBy,
      teamId,
    }: {
      entities: Partial<IEntity>[];
      databaseId: string;
      createdBy?: string;
      teamId?: string;
    }) => {
      return createBulkEntities(entities, databaseId, createdBy, teamId);
    },
    onSuccess: (_, variables) => {
      invalidateAllDatabaseKeys(variables.teamId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate database-level audit logs
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.databaseId),
        exact: false,
      });

      // Invalidate all entity audit logs
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

export const useCreateBulkEntitiesWithoutDuplicateCheck = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      entities,
      databaseId,
      createdBy,
      teamId,
      uploadStartTime,
    }: {
      entities: Partial<IEntity>[];
      databaseId: string;
      createdBy?: string;
      teamId?: string;
      uploadStartTime?: string;
    }) => {
      return createBulkEntitiesWithoutDuplicateCheck(
        entities,
        databaseId,
        createdBy,
        teamId,
        uploadStartTime,
      );
    },
    onSuccess: (_, variables) => {
      invalidateAllDatabaseKeys(variables.teamId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate database-level audit logs
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.databaseId),
        exact: false,
      });

      // Invalidate all entity audit logs
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to update entities status from one status to another
export const useUpdateEntitiesStatusFromTo = (
  targetDatabaseId?: string,
  selectedTeamId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      scope,
      toStatus,
      fromStatus,
      entityIds,
      tab,
      riskLevel,
    }: {
      databaseId: string;
      scope: string;
      toStatus: string;
      fromStatus?: string;
      entityIds?: string[];
      tab?: string;
      riskLevel?: string;
    }) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return updateEntitiesStatusFromTo(
        databaseId,
        scope,
        toStatus,
        selectedTeamId,
        fromStatus,
        entityIds,
        tab,
        riskLevel,
      );
    },
    onSuccess: () => {
      if (targetDatabaseId) {
        invalidateEntityLists(queryClient, targetDatabaseId);
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: entityKeys.all() });
      }

      // Invalidate children cache so expanded child rows reflect bulk status changes
      queryClient.invalidateQueries({
        queryKey: entityKeys.entityChildrenAll(),
        exact: false,
      });

      // Invalidate database-level audit logs
      if (targetDatabaseId) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(targetDatabaseId),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

export const usePreviewEntitiesStatusFromTo = (selectedTeamId?: string) => {
  return useMutation({
    mutationFn: ({
      databaseId,
      scope,
      toStatus,
      fromStatus,
      entityIds,
      tab,
      riskLevel,
    }: {
      databaseId: string;
      scope: string;
      toStatus: string;
      fromStatus?: string;
      entityIds?: string[];
      tab?: string;
      riskLevel?: string;
    }) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return previewEntitiesStatusFromTo(
        databaseId,
        scope,
        toStatus,
        selectedTeamId,
        fromStatus,
        entityIds,
        tab,
        riskLevel,
      );
    },
  });
};

const invalidateAfterPreviousStatusChange = (
  queryClient: ReturnType<typeof useQueryClient>,
  databaseId?: string,
) => {
  queryClient.invalidateQueries({ queryKey: entityKeys.all() });

  if (databaseId) {
    queryClient.invalidateQueries({
      queryKey: auditKeys.auditLogsByDatabase(databaseId),
      exact: false,
    });
  }

  queryClient.invalidateQueries({
    queryKey: [STATS_QUERY_KEYS.stats],
    exact: false,
  });
  queryClient.invalidateQueries({
    queryKey: entityKeys.entityChildrenAll(),
    exact: false,
  });
  queryClient.invalidateQueries({
    queryKey: auditKeys.auditLogs(),
    exact: false,
  });
};

export const useRestorePreviousStatus = (
  databaseId?: string,
  selectedTeamId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return restorePreviousStatus(ids, selectedTeamId);
    },
    onSuccess: () => {
      invalidateAfterPreviousStatusChange(queryClient, databaseId);
    },
  });
};

export const useDeclinePreviousStatus = (
  databaseId?: string,
  selectedTeamId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return declinePreviousStatus(ids, selectedTeamId);
    },
    onSuccess: () => {
      invalidateAfterPreviousStatusChange(queryClient, databaseId);
    },
  });
};

/**
 * One refresh after a bulk action on a selection, whether it succeeded or
 * failed. With a databaseId (the opened Database) only that Database's lists
 * and the team lists; without (Entities Overview) every entity list.
 */
const invalidateAfterSelectionAction = (
  queryClient: ReturnType<typeof useQueryClient>,
  options: { databaseId?: string; databaseCounts: boolean },
) => {
  invalidateEntityLists(queryClient, options.databaseId);
  queryClient.invalidateQueries({ queryKey: entityKeys.entityChildrenAll(), exact: false });
  queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEYS.stats], exact: false });
  queryClient.invalidateQueries({ queryKey: auditKeys.auditLogs(), exact: false });
  if (options.databaseCounts) {
    queryClient.invalidateQueries({ queryKey: databaseKeys.all() });
  }
};

/** Moves selected Database Entities, which may span several Databases, to one Status Flag in one request. */
export const useUpdateDatabaseEntitiesStatus = (
  selectedTeamId?: string,
  databaseId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseEntityIds,
      toStatus,
    }: {
      databaseEntityIds: string[];
      toStatus: StatusFlag;
    }) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return updateDatabaseEntitiesStatus(databaseEntityIds, toStatus, selectedTeamId);
    },
    onSettled: () => {
      invalidateAfterSelectionAction(queryClient, {
        databaseId: databaseId,
        databaseCounts: false,
      });
    },
  });
};

/** Remove from Database for selected Database Entities, which may span several Databases, in one request. */
export const useRemoveDatabaseEntities = (
  selectedTeamId?: string,
  databaseId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (databaseEntityIds: string[]) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return removeDatabaseEntities(databaseEntityIds, selectedTeamId);
    },
    onSettled: () => {
      invalidateAfterSelectionAction(queryClient, {
        databaseId: databaseId,
        databaseCounts: true,
      });
    },
  });
};

export const useRestorePreviousStatusByFlag = (
  databaseId?: string,
  selectedTeamId?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      databaseId?: string;
      groupId?: string;
      previous_status_flag: string;
    }) => {
      if (!selectedTeamId) {
        throw new Error('Team context is required');
      }
      return restorePreviousStatusByFlag({
        ...payload,
        selectedTeamId: selectedTeamId,
      });
    },
    onSuccess: (_data, variables) => {
      invalidateAfterPreviousStatusChange(
        queryClient,
        databaseId || variables.databaseId,
      );
    },
  });
};
